/**
 * Partner dashboard (ORGANIZATION role) — honest scaffold for partner orgs:
 * profile summary from /auth/me plus "Planned — Partner portal" cards.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { authAPI } from '../../../services/api';
import Skeleton from '../../../components/common/Skeleton';
import {
  T, FONT, CARD, I, firstNameOf, greeting,
  FadeSection, Hero, ChartCard, PlannedPill, Avatar,
  QuickActions, PolarStyles,
} from '../shared';

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const firstName = firstNameOf(user);

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    authAPI.me()
      .then(resp => setMe(resp?.data ?? resp ?? null))
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <Skeleton width="100%" height={120} style={{ marginBottom: 18, borderRadius: 20 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Skeleton.Card rows={3} /><Skeleton.Card rows={3} />
      </div>
    </div>
  );

  const name = me?.name || user?.name || 'Partner organisation';
  const roles = me?.roles || user?.roles || [];

  const planned = [
    { Icon: I.shield, color: T.primary, title: 'Partnership Scope',   desc: 'Contracted services, covered regions and referral agreements with Benevita.' },
    { Icon: I.chart,  color: T.teal,    title: 'Service Utilization', desc: 'Volume and outcomes of services delivered to your members, with monthly trends.' },
    { Icon: I.file,   color: T.purple,  title: 'Claims & Reports',    desc: 'Submit and track claims, download utilisation and reconciliation reports.' },
  ];

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>
      <Hero
        index={0}
        roleLabel="Partner portal"
        title={`${greeting()}, ${firstName} 🤝`}
        subtitle="Welcome to the Benevita partner workspace. Your partnership tools are arriving soon — your account is active and ready."
        stats={[{ label: 'Roles', value: roles.length || 1, Icon: I.building }]}
        onRefresh={load}
      />

      <FadeSection index={1} style={{ marginBottom: 18 }}>
        <ChartCard title="Organisation Profile" subtitle="Your account as registered with Benevita">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Avatar name={name} index={0} size={52} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{name}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
                Party ID: {me?.party_id ?? user?.party_id ?? '—'}
                {roles.length ? ` · Roles: ${roles.join(', ')}` : ''}
              </div>
            </div>
          </div>
        </ChartCard>
      </FadeSection>

      <FadeSection index={2} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 18 }}>
        {planned.map(({ Icon, color, title, desc }) => (
          <div key={title} style={{ ...CARD, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12, background: `${color}14` }}>
                <Icon size={20} color={color} />
              </span>
              <PlannedPill label="Planned — Partner portal" />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{title}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4, lineHeight: 1.5 }}>{desc}</div>
            </div>
          </div>
        ))}
      </FadeSection>

      <FadeSection index={3}>
        <QuickActions
          navigate={navigate}
          actions={[{ label: 'My Profile', path: '/profile', color: T.primary, Icon: I.users }]}
        />
      </FadeSection>

      <PolarStyles />
    </div>
  );
}
